import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Waves, Car, ExternalLink, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/site/SectionHeader";
import { LandingShell } from "@/components/site/LandingShell";
import { LandingSeo } from "@/components/site/LandingSeo";
import { LocalGuide } from "@/components/site/LocalGuide";
import { ContactCTA } from "@/components/site/ContactCTA";
import { useT } from "@/lib/i18n";
import { HERO_IMAGE } from "@/lib/siteContent";
import landingContent from "@/lib/landingContent.json";

/**
 * Generic keyword landing page driven by landingContent.json.
 * Blocks are optional per page: `about` (prose), `why` (cards), `water`
 * (distance list), `gettingThere` (travel list), `photos`, `faq`,
 * `crossLinks`. `withGuide` embeds the full 22-place LocalGuide (used on the
 * lake page; the village page keeps its content unique without it).
 */
export const LandingPage = ({ id, withGuide = false }) => {
  const { lang, homePath } = useT();
  const pageSet = landingContent[id] ?? {};
  const page = pageSet[lang] ?? pageSet.sk;

  if (!page) return null;

  return (
    <LandingShell>
      <LandingSeo routeId={id} page={page} />

      {/* Hero: reuses the preloaded homepage hero image */}
      <section id="top" className="relative overflow-hidden isolate min-h-[420px] sm:min-h-[480px]" aria-labelledby={`${id}-title`}>
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMAGE} alt={page.hero.h1} className="h-full w-full object-cover" fetchpriority="high" loading="eager" />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, hsl(24 18% 8% / 0.72) 0%, hsl(24 18% 8% / 0.55) 38%, hsl(24 18% 8% / 0.18) 70%, hsl(24 18% 8% / 0.06) 100%)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, hsl(24 18% 8% / 0.12) 0%, hsl(24 18% 8% / 0.00) 40%, hsl(24 18% 8% / 0.35) 100%)",
            }}
          />
        </div>

        <div className="relative z-10 container-x pt-16 pb-20 sm:pt-20 sm:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="max-w-3xl text-primary-foreground"
            style={{ textShadow: "0 1px 2px rgb(0 0 0 / 0.45), 0 8px 28px rgb(0 0 0 / 0.35)" }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs uppercase tracking-[0.18em] text-white backdrop-blur">
              <Waves className="h-3.5 w-3.5" aria-hidden />
              {page.hero.kicker}
            </div>
            <h1 id={`${id}-title`} className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-white">
              {page.hero.h1}
            </h1>
            <p className="mt-4 max-w-xl text-base sm:text-lg text-white/90 leading-relaxed">{page.hero.subtitle}</p>
            <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <Button asChild size="lg" className="h-12 rounded-xl px-6 text-sm" data-testid={`${id}-primary-cta`}>
                <a href="#kontakt" className="inline-flex items-center gap-2">
                  {page.hero.ctaPrimary}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="secondary"
                size="lg"
                className="h-12 rounded-xl px-6 text-sm bg-white/10 text-white border border-white/25 hover:bg-white/20"
                data-testid={`${id}-secondary-cta`}
              >
                <a href={`${homePath}#ubytovanie`}>{page.hero.ctaSecondary}</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About prose (village page) */}
      {page.about && (
        <section className="section-y" aria-label={page.about.title}>
          <div className="container-x max-w-4xl">
            <SectionHeader kicker={page.about.kicker} title={page.about.title} />
            <div className="mt-6 space-y-4">
              {page.about.paragraphs.map((para, i) => (
                <p key={i} className="text-base text-foreground/85 leading-relaxed">{para}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why cards */}
      {page.why && (
        <section className={`section-y ${page.about ? "bg-secondary/40" : ""}`} aria-label={page.why.title}>
          <div className="container-x">
            <SectionHeader kicker={page.why.kicker} title={page.why.title} />
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {page.why.items.map((w, i) => (
                <motion.article
                  key={w.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: i * 0.06 }}
                  className="rounded-2xl border border-border bg-card p-6 shadow-soft"
                >
                  <h2 className="font-display text-xl font-semibold text-foreground">{w.title}</h2>
                  <p className="mt-2 text-sm text-foreground/80 leading-relaxed">{w.body}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Distance list */}
      {page.water && (
        <section className={`section-y ${page.about ? "" : "bg-secondary/40"}`} aria-label={page.water.title}>
          <div className="container-x max-w-4xl">
            <SectionHeader kicker={page.water.kicker} title={page.water.title} subtitle={page.water.subtitle} />
            <ul className="mt-8 space-y-3" data-testid={`${id}-water-list`}>
              {page.water.items.map((w) => (
                <li key={w.name} className="flex items-start justify-between gap-4 rounded-xl border border-border bg-card p-4 shadow-soft">
                  <div className="min-w-0">
                    <div className="font-medium text-foreground">{w.name}</div>
                    <p className="mt-0.5 text-sm text-foreground/75">{w.note}</p>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-secondary/70 px-2.5 py-1 text-xs font-medium text-foreground tabular-nums">
                    <Car className="h-3 w-3" />
                    {w.distance}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Getting there (village page) */}
      {page.gettingThere && (
        <section className="section-y bg-secondary/40" aria-label={page.gettingThere.title}>
          <div className="container-x max-w-4xl">
            <SectionHeader kicker={page.gettingThere.kicker} title={page.gettingThere.title} />
            <ul className="mt-8 space-y-3">
              {page.gettingThere.items.map((g) => (
                <li key={g.name} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-soft">
                  <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: "hsl(var(--pine))", color: "hsl(var(--primary-foreground))" }}>
                    <MapPin className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="font-medium text-foreground">{g.name}</div>
                    <p className="mt-0.5 text-sm text-foreground/75">{g.value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Full local guide (lake page only, keeps other pages' content unique) */}
      {withGuide && (
        <section className="section-y" aria-label="Local guide">
          <div className="container-x">
            <LocalGuide />
          </div>
        </section>
      )}

      {/* Photo strip */}
      {page.photos && (
        <section className={`section-y ${withGuide ? "bg-secondary/40" : ""}`} aria-label={page.photos.title}>
          <div className="container-x">
            <SectionHeader kicker={page.photos.kicker} title={page.photos.title} />
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
              {page.photos.items.map((p) => (
                <div key={p.src} className="aspect-[4/3] overflow-hidden rounded-xl border border-border bg-card shadow-soft">
                  <img src={p.src} alt={p.alt} loading="lazy" width="900" height="675" className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Page-specific FAQ */}
      {page.faq && (
        <section className="section-y" aria-label={page.faq.title}>
          <div className="container-x max-w-4xl">
            <SectionHeader kicker={page.faq.kicker} title={page.faq.title} />
            <Accordion type="single" collapsible className="mt-8 space-y-3" data-testid={`${id}-faq`}>
              {page.faq.items.map((f, idx) => (
                <AccordionItem
                  key={idx}
                  value={`item-${idx}`}
                  className="rounded-xl border border-border bg-card px-4 shadow-soft data-[state=open]:shadow-lifted"
                >
                  <AccordionTrigger className="text-left text-base font-medium text-foreground hover:no-underline py-4">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-foreground/80 leading-relaxed pb-4">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {page.crossLinks && (
              <div className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-soft">
                <div className="kicker">{page.crossLinks.title}</div>
                <ul className="mt-3 flex flex-col sm:flex-row flex-wrap gap-x-6 gap-y-2">
                  {page.crossLinks.items.map((l) => (
                    <li key={l.href}>
                      <a href={l.href} className="inline-flex items-center gap-1.5 text-sm text-foreground link-underline">
                        {l.label}
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Conversion parity with the homepage (form + availability picker) */}
      <ContactCTA />
    </LandingShell>
  );
};
